import {FROM_DATABASE_DISTANCE_UNITS, SETTING_DISTANCE_UNITS, SETTING_SPEED_UNITS} from "constants/AppConstants.js";
import {Activity, ACTIVITY_DEFAULT_VALUES, calculateBestAvgSpeed} from "services/ActivityService.js";
import {Equipment} from "services/EquipmentService.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {calculateTimeAndDistanceOfPoints, Point} from "services/PointService.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import DirectionUtil from "utils/DirectionUtil.js";
import isNil from "lodash/isNil.js";

export default class ConvertUtil {
  static convertArrayObjectToArray(arrayObject: any): any[] {
    if (!arrayObject) {
      return [];
    }

    const keyIndex = 0;
    const valueIndex = 1;
    const keyValues = Object.entries(arrayObject);
    return keyValues.map((keyValue: [string, any]) => {
      const key = keyValue[keyIndex];
      const value = keyValue[valueIndex];

      return {key: key, ...value};
    });
  }

  static convertLiveArrayObjectToArray(arrayObject: any): any[] {
    const keyIndex = 0;
    const valueIndex = 1;
    const keyValues = Object.entries(arrayObject);
    return keyValues.map((keyValue: [string, any]) => {
      const key = keyValue[keyIndex];
      const value = keyValue[valueIndex].data;

      return {key: key, ...value};
    });
  }

  static convertArrayObjectToKeyValueArray(obj: any): Array<{id: string; value: any}> {
    const array: Array<{id: string; value: any}> = [];
    const keys = Object.keys(obj);

    for (let index = 0; index < keys.length; index++) {
      array.unshift({id: keys[index], value: obj[keys[index]]});
    }

    return array;
  }

  /**
   * Deduplicate points by latitude, longitude and time. Keeps first occurrence;
   * if a duplicate has valid bearing and existing does not, replaces with it.
   * @param {Array} pointsArray - Array of points (db format: l, n, t or Point: latitude, longitude, unixTimestamp/t)
   * @return {Array} Deduplicated array
   */
  static deduplicatePointsByLatLonTime(pointsArray: any[]): any[] {
    if (!pointsArray || pointsArray.length === 0) return pointsArray;

    const uniquePoints: any[] = [];
    const seenKeys = new Set();

    pointsArray.forEach((point) => {
      const timestamp = point.t ?? point.timestamp ?? point.unixTimestamp ?? 0;
      const lat = point.l ?? point.latitude ?? 0;
      const lon = point.n ?? point.longitude ?? 0;
      const key = `${timestamp}-${Number(lat).toFixed(7)}-${Number(lon).toFixed(7)}`;

      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniquePoints.push(point);
      } else {
        const existingIndex = uniquePoints.findIndex((p) => {
          const pT = p.t ?? p.timestamp ?? p.unixTimestamp ?? 0;
          const pLat = p.l ?? p.latitude ?? 0;
          const pLon = p.n ?? p.longitude ?? 0;
          return pT === timestamp && Math.abs(pLat - lat) < 0.0000001 && Math.abs(pLon - lon) < 0.0000001;
        });
        if (existingIndex >= 0) {
          const existing = uniquePoints[existingIndex];
          const existingBearing = existing.r ?? existing.bearing ?? -1;
          const newBearing = point.r ?? point.bearing ?? -1;
          if (newBearing >= 0 && existingBearing < 0) {
            uniquePoints[existingIndex] = point;
          }
        }
      }
    });

    return uniquePoints;
  }

  static convertNavAndPointsToActivity(
    navigation: any,
    points: any,
    customDistanceInMetersForMinTime?: number,
    customDistanceInMetersForAvgSpeed?: number
  ): any {
    if (points === null) {
      points = [];
    }

    if (typeof points === "object") {
      points = ConvertUtil.convertArrayObjectToArray(points);
    }

    points = ConvertUtil.deduplicatePointsByLatLonTime(points);

    if (navigation) {
      return new Activity(navigation, points, customDistanceInMetersForMinTime, customDistanceInMetersForAvgSpeed);
    } else {
      return {};
    }
  }

  static convertGarminActivityToActivity(garminActivity: any, existingPoints?: any[] | null, uid?: string): any {
    /**
     * This function is used to convert a Garmin activity and points to an Activity object
     * @param {Object} garminActivity Garmin activity object from the Garmin Activity Details API endpoint (GET https://apis.garmin.com/wellness-api/rest/activityDetails)
     * @return {Activity} Activity object with navigation and points or null if no garmin activity neither the activity is not a watersport activity
     */
    const navigation: any = new Activity({}, null);
    let points: any[] = [];

    if (garminActivity) {
      navigation.garminActivityId = garminActivity.activityId;
      navigation.name = garminActivity.summary?.activityName ?? garminActivity.activityName;
      navigation.waterSportId = navigation.mapGarminWaterSportToWaterSportId(
        garminActivity.summary?.activityType ?? garminActivity.activityType
      );
      if (isNil(navigation.waterSportId)) {
        console.warn("Activity is not a watersport activity", {
          uid,
          garminActivityId: navigation.garminActivityId,
          type: garminActivity.summary?.activityType ?? garminActivity.activityType
        });
        return {navigation: null, filteredOutPoints: []};
      }
      navigation.totalDistanceInKm =
        garminActivity.summary?.distanceInMeters || garminActivity.distanceInMeters
          ? ConvertUtil.convertMeterToKiloMeter(
              garminActivity.summary?.distanceInMeters ?? garminActivity.distanceInMeters
            )
          : null;
      navigation.durationInSeconds = garminActivity.summary?.durationInSeconds ?? garminActivity.durationInSeconds;
      navigation.pausedTimeInSeconds = 0;
      navigation.originalDateInUTC = DateTimeUtil.getDateByTimeStamp(
        garminActivity.summary?.startTimeInSeconds ?? garminActivity.startTimeInSeconds
      );
      navigation.averageSpeedInKM =
        garminActivity.summary?.averageSpeedInMetersPerSecond || garminActivity.averageSpeedInMetersPerSecond
          ? ConvertUtil.convertMPSToKMH(
              garminActivity.summary?.averageSpeedInMetersPerSecond ?? garminActivity.averageSpeedInMetersPerSecond
            )
          : null;
      navigation.maxSpeedInKM =
        garminActivity.summary?.maxSpeedInMetersPerSecond || garminActivity.maxSpeedInMetersPerSecond
          ? ConvertUtil.convertMPSToKMH(
              garminActivity.summary?.maxSpeedInMetersPerSecond ?? garminActivity.maxSpeedInMetersPerSecond
            )
          : null;
      navigation.feelingId = ACTIVITY_DEFAULT_VALUES.feelingId;
      navigation.fnt = "garmin";

      let index = 0;
      garminActivity.samples?.forEach((sample) => {
        if (sample.latitudeInDegree && sample.longitudeInDegree) {
          const point = new Point({});
          point.latitude = sample.latitudeInDegree;
          point.longitude = sample.longitudeInDegree;
          point.elevationInMeter = sample.elevationInMeters;
          point.speedInMPS = sample.speedMetersPerSecond;
          point.unixTimestamp = sample.startTimeInSeconds;
          point.id = "point" + (index + 1);
          point.heartRate = sample.heartRate;
          point.bearing =
            index === 0 ||
            !garminActivity.samples[index - 1].latitudeInDegree ||
            !garminActivity.samples[index - 1].longitudeInDegree ||
            !sample.latitudeInDegree ||
            !sample.longitudeInDegree
              ? -1
              : DirectionUtil.getBearingValue(
                  garminActivity.samples[index - 1].latitudeInDegree,
                  garminActivity.samples[index - 1].longitudeInDegree,
                  sample.latitudeInDegree,
                  sample.longitudeInDegree
                );
          points.push(point);
          index++;
        }
      });

      if (existingPoints && existingPoints.length > 0) {
        points = existingPoints.map((point) => {
          return new Point(point);
        });
      }

      // Remove undefined points from the array
      points = points.filter((point) => point);

      if (points?.length > 0) {
        points[points.length - 1].calories =
          garminActivity.summary?.activeKilocalories ?? garminActivity.activeKilocalories;

        navigation.points = points;
        navigation.bestSpeedFiveSecondsKM = calculateBestAvgSpeed(points, 5, 0, SETTING_SPEED_UNITS.km_slash_h)[0];
        const coords = navigation.getCoordsFromPoints(navigation.points);
        navigation.linearDistance = CoordinateUtil.calculateLinearDistance(coords, SETTING_DISTANCE_UNITS.kilometers);
        navigation.latitude = navigation.points?.[0]?.latitude;
        navigation.longitude = navigation.points?.[0]?.longitude;
        navigation.bearing = navigation.points
          ? DirectionUtil.getBearing(
              navigation.points[0].latitude,
              navigation.points[0].longitude,
              navigation.points[navigation.points.length - 1].latitude,
              navigation.points[navigation.points.length - 1].longitude
            )
          : 0;

        if (!navigation.totalDistanceInKm) {
          const computedPoints = calculateTimeAndDistanceOfPoints(navigation.points);
          navigation.totalDistanceInKm = computedPoints[computedPoints.length - 1].distanceFromStartPointInMeter / 1000;
        }

        if (!navigation.averageSpeedInKM) {
          navigation.averageSpeedInKM = ConvertUtil.convertMPSToKMH(
            navigation.points.reduce((acc, point) => {
              acc += point.speedInMPS;
              return acc;
            }, 0) / navigation.points.length
          );
        }

        if (!navigation.maxSpeedInKM) {
          navigation.maxSpeedInKM = ConvertUtil.convertMPSToKMH(
            navigation.points.reduce((acc, point) => {
              acc = Math.max(acc, point.speedInMPS);
              return acc;
            }, 0)
          );
        }
      }
      return navigation;
    }

    return null;
  }

  static convertLivePointsToActivity(points: any): any {
    if (points === null) {
      points = [];
    }

    if (typeof points === "object") {
      points = ConvertUtil.convertLiveArrayObjectToArray(points);
    }

    const navigation = [];

    return new Activity(navigation, points);
  }

  static convertEquipmentList(equipments: any[] | null): any[] {
    if (equipments === null) {
      equipments = [];
    }

    const equipList: any[] = [];

    if (typeof equipments === "object") {
      equipments.forEach((equipsObject) => {
        const equips = ConvertUtil.convertArrayObjectToArray(equipsObject);
        equips.forEach((equip) => {
          const e = new Equipment(equip);
          if (!e.del) {
            equipList.push(e);
          }
        });
      });
    }

    return equipList;
  }

  static convertNavsToActivities(navigations: any[]): any[] {
    if (!navigations || navigations.length === 0) {
      return [];
    }

    return navigations.map((navigation) => ConvertUtil.convertNavAndPointsToActivity(navigation, []));
  }

  static convertSpeedInKM(speed: number, speedUnit: string): number {
    let output: number;

    const KNOTS_CONVERSION_FACTOR = 1.852;
    const MILES_CONVERSION_FACTOR = 1.609;
    const METER_PER_SECOND_TO_KILOMETER_PER_HOUR = 3.6;

    switch (speedUnit) {
      case SETTING_SPEED_UNITS.km_slash_h:
        output = speed;
        break;
      case SETTING_SPEED_UNITS.knots:
        output = speed * KNOTS_CONVERSION_FACTOR;
        break;
      case SETTING_SPEED_UNITS.mph:
        output = speed * MILES_CONVERSION_FACTOR;
        break;
      case SETTING_SPEED_UNITS.m_slash_s:
        output = speed * METER_PER_SECOND_TO_KILOMETER_PER_HOUR;
        break;
      default:
        throw new Error(`invalid speed unit ${speedUnit}`);
    }

    return output;
  }

  static convertSpeed(speedInKM: number, speedUnit: string): number {
    let output: number;

    const KNOTS_CONVERSION_FACTOR = 1.852;
    const MILES_CONVERSION_FACTOR = 1.609;
    const KILOMETER_PER_HOUR_TO_METER_PER_SECOND = 3.6;

    if (speedInKM === 0) {
      return 0;
    }

    switch (speedUnit) {
      case SETTING_SPEED_UNITS.km_slash_h:
        output = speedInKM;
        break;
      case SETTING_SPEED_UNITS.knots:
        output = speedInKM / KNOTS_CONVERSION_FACTOR;
        break;
      case SETTING_SPEED_UNITS.mph:
        output = speedInKM / MILES_CONVERSION_FACTOR;
        break;
      case SETTING_SPEED_UNITS.m_slash_s:
        output = speedInKM / KILOMETER_PER_HOUR_TO_METER_PER_SECOND;
        break;
      default:
        throw new Error(`invalid speed unit ${speedUnit}`);
    }

    return output;
  }

  static convertDistance(distanceInKM: number, distanceUnit: string): number {
    let output: number;

    const MILES_CONVERSION_FACTOR = 0.6213712;
    const NAUTICAL_MILES_CONVERSION_FACTOR = 1.852;

    switch (distanceUnit) {
      case SETTING_DISTANCE_UNITS.kilometers:
        output = distanceInKM;
        break;
      case SETTING_DISTANCE_UNITS.miles:
        output = distanceInKM * MILES_CONVERSION_FACTOR;
        break;
      case SETTING_DISTANCE_UNITS.n_dot_Miles:
        output = distanceInKM / NAUTICAL_MILES_CONVERSION_FACTOR;
        break;
      case FROM_DATABASE_DISTANCE_UNITS[0]:
        // Meters
        output = distanceInKM * 1000;
        break;
      default:
        output = distanceInKM;
        break;
    }

    return output;
  }

  static convertDistanceToKM(distance: number, distanceUnit: string): number {
    let output: number;

    const MILES_CONVERSION_FACTOR = 0.6213712;
    const NAUTICAL_MILES_CONVERSION_FACTOR = 1.852;

    switch (distanceUnit) {
      case SETTING_DISTANCE_UNITS.kilometers:
        output = distance;
        break;
      case SETTING_DISTANCE_UNITS.miles:
        output = distance / MILES_CONVERSION_FACTOR;
        break;
      case SETTING_DISTANCE_UNITS.n_dot_Miles:
        output = distance * NAUTICAL_MILES_CONVERSION_FACTOR;
        break;
      case FROM_DATABASE_DISTANCE_UNITS[0]:
        // Meters
        output = distance / 1000;
        break;
      default:
        throw new Error(`invalid distance unit: ${distanceUnit}`);
    }

    return output;
  }

  static convertMPSToKMH(value: number): number {
    return value * 3.6;
  }

  static convertMeterToKiloMeter(value: number): number {
    return value / 1000;
  }

  static convertKiloMeterToMeter(value: number): number {
    return value / 1000;
  }

  static convertKnotTokilometer(value: number): number {
    const KNOTS_CONVERSION_FACTOR = 1.852;
    return value * KNOTS_CONVERSION_FACTOR;
  }

  static convertDegreeToRadian = function (degrees: number): number {
    return degrees * (Math.PI / 180);
  };

  static convertRadianToDegree = function (radians: number): number {
    return radians * (180 / Math.PI);
  };

  static convertDegreeToCartesian = function (degrees: number): {x: number; y: number} {
    return {
      x: Math.cos((degrees * Math.PI) / 180),
      y: Math.sin((degrees * Math.PI) / 180)
    };
  };

  static convertCartesianToDegree = function (x: number, y: number): number {
    return (Math.atan2(y, x) * 180) / Math.PI;
  };
}
